import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  ArrowsClockwise, MapPin, Clock, Users, UserPlus, Globe,
  CaretRight, CaretDown,
} from "@phosphor-icons/react";
import WidgetIntelligence from "./intelligence/WidgetIntelligence";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtNum = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));
const fmtMoney = (n, c = "INR") => {
  if (!n && n !== 0) return "-";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${c} ${fmtNum(n)}`;
  }
};

const COUNTRY_FLAG = (cc) => {
  if (!cc || cc.length !== 2) return "";
  const codePoints = cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const Section = ({ title, subtitle, icon: Icon, color, children, defaultOpen = true, testid, refreshing = false, refreshingLabel = "Refreshing…" }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card-flat overflow-hidden" data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-[#161618] text-left"
      >
        {Icon && (
          <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: `${color}22` }}>
            <Icon size={18} weight="bold" style={{ color }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            {title}
            {refreshing && (
              <span
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{ background: "#FFEB0033", color: "#FFEB00" }}
                data-testid={`${testid}-refreshing`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFEB00] animate-pulse" />
                {refreshingLabel}
              </span>
            )}
          </div>
          {subtitle && <div className="text-[10px] text-[#71717A]">{subtitle}</div>}
        </div>
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
      </button>
      {open && <div className="p-4 pt-0">{children}</div>}
    </div>
  );
};

const CommerceInsights = ({ clientId, currency, month, dateRange, readOnly = false, commerceIntel, customersIntel, loadingIntel }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null); // null | { kind: 'category'|'collection', name: string }

  // True when the parent has activated a sub-month range (Today/7d/30d).
  // We slice daily_revenue locally; other sections (best sellers, geography, etc.)
  // remain monthly aggregates because they can't be sliced from the cached doc.
  const isRangeMode = !!dateRange && dateRange.preset !== "this_month";

  // Range-aware daily_revenue slice + recomputed totals for the chart subtitle
  const slicedDaily = useMemo(() => {
    if (!data?.daily_revenue) return null;
    if (!isRangeMode) return data.daily_revenue;
    return data.daily_revenue.filter((d) => {
      const dt = d.date;
      return dt >= dateRange.start_date && dt <= dateRange.end_date;
    });
  }, [data, isRangeMode, dateRange]);

  const rangeTotals = useMemo(() => {
    if (!slicedDaily) return null;
    let rev = 0, ord = 0;
    for (const d of slicedDaily) {
      rev += Number(d.revenue) || 0;
      ord += Number(d.orders) || 0;
    }
    return { revenue: rev, orders: ord, aov: ord ? rev / ord : 0 };
  }, [slicedDaily]);

  // Tracks (clientId, range_key) we've already attempted to auto-sync this session,
  // so toggling between presets doesn't kick off duplicate syncs.
  const autoSyncedRef = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (isRangeMode) {
        params.start_date = dateRange.start_date;
        params.end_date = dateRange.end_date;
      }
      const res = await axios.get(`${API}/clients/${clientId}/shopify/commerce-insights`, { params });
      const got = res.data?.data || null;
      setData(got);
      // Auto-sync: if user has just switched to a sub-month range that has no
      // cached doc yet, kick off the sync transparently — the dashboard will
      // refresh as soon as it's ready. No "click Sync" friction.
      if (!got && isRangeMode) {
        const key = `${clientId}|${month}|${dateRange.start_date}_${dateRange.end_date}`;
        if (!autoSyncedRef.current.has(key)) {
          autoSyncedRef.current.add(key);
          // fire-and-forget; sync() handles its own loading/error state
          // and re-loads on success.
          sync({ auto: true });
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load insights");
    } finally {
      setLoading(false);
    }
  }, [clientId, month, isRangeMode, dateRange?.start_date, dateRange?.end_date]);

  useEffect(() => { load(); }, [load]);

  const sync = async ({ auto = false } = {}) => {
    setSyncing(true);
    setError(null);
    setInfo(null);
    try {
      const params = { month };
      if (isRangeMode) {
        params.start_date = dateRange.start_date;
        params.end_date = dateRange.end_date;
      }
      const res = await axios.post(`${API}/clients/${clientId}/shopify/commerce-insights`, null, {
        params, timeout: 240000,
      });
      const r = res.data || {};
      const scope = isRangeMode ? `${dateRange.label} (${dateRange.start_date} → ${dateRange.end_date})` : month;
      const prefix = auto ? "Auto-synced" : "Synced";
      setInfo(`${prefix} ${scope}: ${r.geography_rows || 0} countries · ${r.total_customers || 0} unique customers (${r.new_customers} new, ${r.returning_customers} returning) · ${r.best_sellers || 0} best sellers`);
      setTimeout(() => setInfo(null), 8000);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const totalCountryRevenue = useMemo(
    () => (data?.geography || []).reduce((s, g) => s + g.revenue, 0),
    [data],
  );
  const heatmapMax = useMemo(() => {
    if (!data?.heatmap) return 0;
    let m = 0;
    for (const row of data.heatmap) for (const cell of row) if (cell.revenue > m) m = cell.revenue;
    return m;
  }, [data]);

  const bestSlot = useMemo(() => {
    if (!data?.heatmap) return null;
    let best = { day: 0, hour: 0, revenue: 0 };
    data.heatmap.forEach((row, d) => {
      row.forEach((cell, h) => {
        if (cell.revenue > best.revenue) best = { day: d, hour: h, revenue: cell.revenue };
      });
    });
    return best.revenue > 0 ? best : null;
  }, [data]);

  const timeInsights = useMemo(() => {
    if (!data?.heatmap) return null;
    // Aggregate per day-of-week and per hour (across all days)
    const byDay = Array.from({ length: 7 }, () => ({ revenue: 0, orders: 0 }));
    const byHour = Array.from({ length: 24 }, () => ({ revenue: 0, orders: 0 }));
    let totalRev = 0;
    let totalOrd = 0;
    data.heatmap.forEach((row, d) => {
      row.forEach((cell, h) => {
        byDay[d].revenue += cell.revenue;
        byDay[d].orders += cell.orders;
        byHour[h].revenue += cell.revenue;
        byHour[h].orders += cell.orders;
        totalRev += cell.revenue;
        totalOrd += cell.orders;
      });
    });
    if (totalRev === 0) return null;
    const bestDay = byDay
      .map((v, i) => ({ idx: i, ...v }))
      .sort((a, b) => b.revenue - a.revenue)[0];
    const bestHour = byHour
      .map((v, i) => ({ idx: i, ...v }))
      .sort((a, b) => b.revenue - a.revenue)[0];
    // 3-hour peak window centred on best hour
    const h = bestHour.idx;
    const winStart = (h - 1 + 24) % 24;
    const winEnd = (h + 2) % 24;
    return {
      bestDay: { idx: bestDay.idx, revenue: bestDay.revenue, orders: bestDay.orders },
      bestHour: { idx: bestHour.idx, revenue: bestHour.revenue, orders: bestHour.orders },
      window: `${String(winStart).padStart(2, "0")}:00–${String(winEnd).padStart(2, "0")}:00`,
      dayShare: totalRev > 0 ? (bestDay.revenue / totalRev) * 100 : 0,
      hourShare: totalRev > 0 ? (bestHour.revenue / totalRev) * 100 : 0,
    };
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#E4E4E7] flex items-center gap-2">
          <Globe size={18} weight="bold" style={{ color: "#10B981" }} />
          Commerce Insights — Geography · Time · Customers
          {data?.synced_at && (
            <span className="text-[10px] text-[#71717A] font-normal">
              · synced {new Date(data.synced_at).toLocaleString()}
            </span>
          )}
        </h3>
        <button
          onClick={sync}
          disabled={syncing}
          className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5"
          style={{ background: syncing ? "#52525B" : "#10B981", color: "#fff" }}
          data-testid="sync-commerce-insights-btn"
        >
          <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Syncing Shopify orders…" : "Sync Commerce Insights"}
        </button>
      </div>

      {error && <div className="p-2 rounded text-xs" style={{ background: "#FF444422", color: "#FF6666" }}>{error}</div>}
      {info && <div className="p-2 rounded text-xs" style={{ background: "#10B98122", color: "#6EE7B7" }}>{info}</div>}

      {isRangeMode && (
        <div
          className="p-3 rounded text-[11px] flex items-start gap-2"
          style={{
            background: syncing ? "#FFEB0011" : (data ? "#10B98111" : "#FFEB0011"),
            border: syncing ? "1px solid #FFEB0066" : (data ? "1px solid #10B98144" : "1px solid #FFEB0044"),
            color: syncing ? "#FFEB00" : (data ? "#6EE7B7" : "#FFEB00"),
          }}
          data-testid="commerce-range-banner"
        >
          <span>{syncing ? "⏳" : (data ? "📅" : "🔄")}</span>
          <span className="flex-1">
            <strong>{dateRange.label}</strong> ({dateRange.start_date} → {dateRange.end_date})
            {syncing ? (
              <span className="text-white/80 ml-1">
                · Refreshing Commerce, Funnel, Products, Customers, Geography for this range — usually 15-30 seconds…
              </span>
            ) : data ? (
              <span className="text-white/80 ml-1">
                · All sections below (Best Sellers, Geography, Heatmap, Customer Split) are computed for this range.
              </span>
            ) : (
              <span className="text-white/80 ml-1">
                · Preparing range data — auto-syncing now…
              </span>
            )}
          </span>
        </div>
      )}

      {loading && !data ? (
        <div className="card-flat p-8 text-center text-xs text-[#71717A]">Loading commerce insights…</div>
      ) : !data ? (
        <div className="card-flat p-8 text-center text-xs">
          <div className="text-[#A1A1AA] mb-2">No commerce insights yet for {month}.</div>
          {!readOnly && (
            <button
              onClick={sync}
              disabled={syncing}
              className="px-3 py-1.5 rounded text-xs font-medium"
              style={{ background: "#10B981", color: "#fff" }}
            >
              Run Commerce Insights sync
            </button>
          )}
        </div>
      ) : (
        <>
          {/* AI Intelligence · Commerce — top-level summary + opp/risk/rec */}
          <WidgetIntelligence
            widget={commerceIntel}
            label={loadingIntel ? "AI Intelligence · Generating…" : "AI Intelligence · Commerce"}
            testid="widget-intel-commerce"
          />

          {/* Daily Revenue Trend — at the very top per user request */}
          <Section
            title="Daily Revenue Trend"
            subtitle={
              isRangeMode && rangeTotals
                ? `${slicedDaily?.length || 0} days · ${dateRange.label} (${dateRange.start_date} → ${dateRange.end_date}) · ${fmtMoney(rangeTotals.revenue, currency)} · ${fmtNum(rangeTotals.orders)} orders`
                : `${data.daily_revenue?.length || 0} days · day-by-day Shopify revenue + orders for ${month}`
            }
            icon={Globe} color="#10B981"
            testid="daily-revenue-section"
            refreshing={syncing}
            refreshingLabel="Updating Revenue…"
          >
            <DailyRevenueBars daily={slicedDaily || data.daily_revenue || []} currency={currency} />
          </Section>

          {/* Best Sellers */}
          {data.best_sellers && data.best_sellers.length > 0 && (() => {
            const filteredSellers = categoryFilter
              ? data.best_sellers.filter((p) =>
                  categoryFilter.kind === "collection"
                    ? Array.isArray(p.collections) && p.collections.includes(categoryFilter.name)
                    : (p.product_type || "Uncategorized") === categoryFilter.name
                )
              : data.best_sellers;
            const filterLabel = categoryFilter ? `${categoryFilter.kind === "collection" ? "Collection" : "Category"}: ${categoryFilter.name}` : "";
            return (
              <Section
                title={categoryFilter ? `Best Sellers · ${categoryFilter.name}` : "Best Selling Products"}
                subtitle={
                  categoryFilter
                    ? `${filteredSellers.length} products in "${filterLabel}" · click × to clear`
                    : `Top ${Math.min(data.best_sellers.length, 12)} of ${data.best_sellers.length} products with sales in ${isRangeMode ? dateRange.label.toLowerCase() : month}`
                }
                icon={Globe} color="#10B981"
                testid="best-sellers-section"
                refreshing={syncing}
                refreshingLabel="Updating Products…"
              >
                {categoryFilter && (
                  <div className="mb-3 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-[#71717A]">Filter:</span>
                    <button
                      onClick={() => setCategoryFilter(null)}
                      className="px-2 py-1 rounded-full text-[10px] font-semibold flex items-center gap-1.5"
                      style={{ background: "#EC489922", color: "#F9A8D4" }}
                      data-testid="clear-category-filter-btn"
                    >
                      {filterLabel}
                      <span className="text-[#F9A8D4] hover:text-white">×</span>
                    </button>
                  </div>
                )}
                {filteredSellers.length === 0 ? (
                  <div className="text-xs text-[#71717A]">No products found in this {categoryFilter?.kind || "group"}.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {filteredSellers.slice(0, 12).map((p, i) => (
                      <BestSellerCard key={p.id} product={p} rank={i + 1} currency={currency} />
                    ))}
                  </div>
                )}
              </Section>
            );
          })()}

          {/* Top Collections + Top Categories — side by side on large screens */}
          {((data.collections && data.collections.length > 0) || (data.categories && data.categories.length > 0)) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="collections-categories-row">
              {data.collections && data.collections.length > 0 && (
                <Section
                  title="Top Collections"
                  subtitle={`${data.collections.length} Shopify collections · click a bar to filter`}
                  icon={Globe} color="#EC4899"
                  testid="collections-section"
                  refreshing={syncing}
                  refreshingLabel="Updating Collections…"
                >
                  <CategoryBars
                    categories={data.collections}
                    currency={currency}
                    activeCategory={categoryFilter?.kind === "collection" ? categoryFilter.name : null}
                    onSelect={(name) =>
                      setCategoryFilter((cur) =>
                        cur && cur.kind === "collection" && cur.name === name ? null : { kind: "collection", name }
                      )
                    }
                    gradient="linear-gradient(90deg, #EC4899, #F97316)"
                  />
                </Section>
              )}

              {data.categories && data.categories.length > 0 && (
                <Section
                  title="Top Categories"
                  subtitle={`${data.categories.length} categories (Shopify product type) · click to filter`}
                  icon={Globe} color="#06B6D4"
                  testid="categories-section"
                  refreshing={syncing}
                  refreshingLabel="Updating Categories…"
                >
                  <CategoryBars
                    categories={data.categories}
                    currency={currency}
                    activeCategory={categoryFilter?.kind === "category" ? categoryFilter.name : null}
                    onSelect={(name) =>
                      setCategoryFilter((cur) =>
                        cur && cur.kind === "category" && cur.name === name ? null : { kind: "category", name }
                      )
                    }
                    gradient="linear-gradient(90deg, #06B6D4, #3B82F6)"
                  />
                </Section>
              )}
            </div>
          )}

          {/* AI Intelligence · Customers — scoped insight strip */}
          <WidgetIntelligence
            widget={customersIntel}
            label="AI Intelligence · Customers"
            testid="widget-intel-customers"
          />

          {/* Customer split */}
          <Section
            title="New vs Returning Customers"
            subtitle={`${fmtNum(data.customers.total_unique)} unique customers in ${isRangeMode ? dateRange.label.toLowerCase() : month}`}
            icon={Users} color="#A855F7"
            testid="customers-section"
            refreshing={syncing}
            refreshingLabel="Updating Customers…"
          >
            <CustomerSplit c={data.customers} currency={currency} />
          </Section>

          {/* Geography */}
          <Section
            title="Geography — Revenue by Country"
            subtitle={`${data.geography.length} countries`}
            icon={MapPin} color="#3B82F6"
            testid="geography-section"
            refreshing={syncing}
            refreshingLabel="Updating Geography…"
          >
            {data.geography.length === 0 ? (
              <div className="text-xs text-[#71717A]">No country data — orders may not have shipping addresses recorded.</div>
            ) : (
              <div className="space-y-1.5">
                {data.geography.slice(0, 15).map((g) => {
                  const pct = totalCountryRevenue > 0 ? (g.revenue / totalCountryRevenue) * 100 : 0;
                  return (
                    <div key={g.country_code} className="flex items-center gap-3 p-2 rounded bg-[#0A0A0C]" data-testid="geo-row">
                      <div className="w-32 shrink-0 text-xs font-semibold flex items-center gap-1.5">
                        <span className="text-base">{COUNTRY_FLAG(g.country_code)}</span>
                        <span className="truncate">{g.country}</span>
                      </div>
                      <div className="flex-1 h-6 bg-[#1F1F22] rounded overflow-hidden relative">
                        <div
                          className="h-full flex items-center px-2 text-[10px] font-semibold"
                          style={{
                            width: `${Math.min(100, pct)}%`,
                            background: "linear-gradient(90deg, #3B82F6, #06B6D4)",
                            color: "#fff",
                          }}
                        >
                          {pct >= 8 ? `${pct.toFixed(1)}%` : ""}
                        </div>
                        {pct < 8 && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A1A1AA]">
                            {pct.toFixed(1)}%
                          </span>
                        )}
                      </div>
                      <div className="w-24 text-right text-xs data-number" style={{ color: "#86EFAC" }}>
                        {fmtMoney(g.revenue, currency)}
                      </div>
                      <div className="w-16 text-right text-[10px] text-[#A1A1AA]">{fmtNum(g.orders)} ord</div>
                      <div className="w-16 text-right text-[10px] text-[#A1A1AA]">{fmtNum(g.customers)} cust</div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Time-of-day heatmap */}
          <Section
            title="When Are People Buying?"
            subtitle={bestSlot ? `Peak cell: ${DAY_LABELS[bestSlot.day]} ${bestSlot.hour}:00 — ${fmtMoney(bestSlot.revenue, currency)} revenue` : "Day × Hour heatmap (IST)"}
            icon={Clock} color="#F59E0B"
            testid="heatmap-section"
            refreshing={syncing}
            refreshingLabel="Updating Heatmap…"
          >
            {timeInsights && (
              <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="time-insight-panel">
                <div className="card-flat p-3" style={{ background: "#10B98111", border: "1px solid #10B98133" }}>
                  <div className="text-[10px] text-[#71717A] uppercase tracking-wider">Best day</div>
                  <div className="text-lg font-bold" style={{ color: "#6EE7B7" }}>{DAY_LABELS[timeInsights.bestDay.idx]}</div>
                  <div className="text-[11px] text-[#A1A1AA]">
                    {fmtMoney(timeInsights.bestDay.revenue, currency)} · {fmtNum(timeInsights.bestDay.orders)} orders · {timeInsights.dayShare.toFixed(0)}% of week
                  </div>
                </div>
                <div className="card-flat p-3" style={{ background: "#F59E0B11", border: "1px solid #F59E0B33" }}>
                  <div className="text-[10px] text-[#71717A] uppercase tracking-wider">Peak hour</div>
                  <div className="text-lg font-bold" style={{ color: "#FCD34D" }}>{String(timeInsights.bestHour.idx).padStart(2, "0")}:00</div>
                  <div className="text-[11px] text-[#A1A1AA]">
                    {fmtMoney(timeInsights.bestHour.revenue, currency)} · {fmtNum(timeInsights.bestHour.orders)} orders · {timeInsights.hourShare.toFixed(0)}% of day
                  </div>
                </div>
                <div className="card-flat p-3" style={{ background: "#A855F711", border: "1px solid #A855F733" }}>
                  <div className="text-[10px] text-[#71717A] uppercase tracking-wider">Peak window (IST)</div>
                  <div className="text-lg font-bold" style={{ color: "#D8B4FE" }}>{timeInsights.window}</div>
                  <div className="text-[11px] text-[#A1A1AA]">Schedule ads/push campaigns to land in this 3-hour window on {DAY_LABELS[timeInsights.bestDay.idx]}s</div>
                </div>
              </div>
            )}
            <Heatmap data={data.heatmap} max={heatmapMax} currency={currency} />
            <HourlyChart hourly={data.hourly_revenue} currency={currency} />
          </Section>
        </>
      )}
    </div>
  );
};

const CustomerSplit = ({ c, currency }) => {
  const newC = c.new || {};
  const retC = c.returning || {};
  const totalRevenue = (newC.revenue || 0) + (retC.revenue || 0);
  const newPct = totalRevenue > 0 ? (newC.revenue / totalRevenue) * 100 : 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="card-flat p-3" style={{ background: "#3B82F611", border: "1px solid #3B82F633" }}>
          <div className="flex items-center gap-2 mb-2">
            <UserPlus size={14} style={{ color: "#93C5FD" }} />
            <span className="text-xs font-semibold text-[#93C5FD]">New Customers</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div><div className="text-[#71717A]">Customers</div><div className="data-number text-base">{fmtNum(newC.customers)}</div></div>
            <div><div className="text-[#71717A]">Orders</div><div className="data-number text-base">{fmtNum(newC.orders)}</div></div>
            <div><div className="text-[#71717A]">Revenue</div><div className="data-number text-base" style={{ color: "#86EFAC" }}>{fmtMoney(newC.revenue, currency)}</div></div>
            <div><div className="text-[#71717A]">AOV</div><div className="data-number text-base">{fmtMoney(newC.aov, currency)}</div></div>
          </div>
        </div>
        <div className="card-flat p-3" style={{ background: "#A855F711", border: "1px solid #A855F733" }}>
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} style={{ color: "#D8B4FE" }} />
            <span className="text-xs font-semibold text-[#D8B4FE]">Returning Customers</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div><div className="text-[#71717A]">Customers</div><div className="data-number text-base">{fmtNum(retC.customers)}</div></div>
            <div><div className="text-[#71717A]">Orders</div><div className="data-number text-base">{fmtNum(retC.orders)}</div></div>
            <div><div className="text-[#71717A]">Revenue</div><div className="data-number text-base" style={{ color: "#86EFAC" }}>{fmtMoney(retC.revenue, currency)}</div></div>
            <div><div className="text-[#71717A]">AOV</div><div className="data-number text-base">{fmtMoney(retC.aov, currency)}</div></div>
          </div>
        </div>
      </div>

      {/* Revenue split bar */}
      {totalRevenue > 0 && (
        <div>
          <div className="flex justify-between text-[10px] text-[#71717A] mb-1">
            <span>Revenue contribution</span>
            <span>New {newPct.toFixed(0)}% · Returning {(100 - newPct).toFixed(0)}%</span>
          </div>
          <div className="flex h-3 rounded overflow-hidden">
            <div style={{ width: `${newPct}%`, background: "#3B82F6" }} title={`New: ${fmtMoney(newC.revenue, currency)}`} />
            <div style={{ width: `${100 - newPct}%`, background: "#A855F7" }} title={`Returning: ${fmtMoney(retC.revenue, currency)}`} />
          </div>
        </div>
      )}

      <div className="text-[10px] text-[#71717A]">
        AOV gap: Returning customers spend{" "}
        {newC.aov > 0 ? (
          retC.aov >= newC.aov
            ? <span className="text-[#86EFAC]">{(((retC.aov - newC.aov) / newC.aov) * 100).toFixed(0)}% more</span>
            : <span className="text-[#FF9999]">{(((newC.aov - retC.aov) / newC.aov) * 100).toFixed(0)}% less</span>
        ) : "—"}{" "}
        per order than new customers.
      </div>
    </div>
  );
};

const Heatmap = ({ data, max, currency }) => {
  if (!data || max === 0) {
    return <div className="text-xs text-[#71717A]">No timestamp data available for this period.</div>;
  }
  // Thresholds for the 4-stop heatmap: worst (red) → low (orange) → average (yellow) → best (green)
  // Anything >0 but <20% of max = red (cold)
  // 20-50% = orange (below average)
  // 50-80% = yellow (average/good)
  // >80% = green (peak)
  const cellColor = (v) => {
    if (!v) return "#0A0A0C";
    const r = v / max;
    if (r >= 0.8) return "#10B981";   // emerald — peak
    if (r >= 0.5) return "#84CC16";   // lime — strong
    if (r >= 0.25) return "#F59E0B";  // amber — average
    if (r >= 0.1) return "#F97316";   // orange — below average
    return "#EF4444";                  // red — weak
  };
  const legend = [
    { label: "Weak", color: "#EF4444" },
    { label: "Low", color: "#F97316" },
    { label: "Avg", color: "#F59E0B" },
    { label: "Strong", color: "#84CC16" },
    { label: "Peak", color: "#10B981" },
  ];
  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="flex gap-1 mb-1 ml-10">
          {Array.from({ length: 24 }).map((_, h) => (
            <div key={h} className="text-[8px] text-[#52525B] text-center" style={{ width: 22 }}>
              {h % 3 === 0 ? `${h}h` : ""}
            </div>
          ))}
        </div>
        {data.map((row, d) => (
          <div key={d} className="flex items-center gap-1 mb-0.5">
            <div className="w-8 text-[10px] text-[#A1A1AA] text-right pr-1">{DAY_LABELS[d]}</div>
            {row.map((cell, h) => (
              <div
                key={h}
                title={`${DAY_LABELS[d]} ${h}:00 — ${fmtMoney(cell.revenue, currency)} · ${cell.orders} orders`}
                style={{
                  width: 22, height: 18,
                  background: cellColor(cell.revenue),
                  borderRadius: 2,
                }}
                className="hover:ring-1 hover:ring-white cursor-default"
              />
            ))}
          </div>
        ))}
        <div className="flex items-center gap-2 mt-2 text-[10px] text-[#71717A]">
          <span>Weak</span>
          {legend.map((l, i) => (
            <div key={i} title={l.label} style={{ width: 18, height: 12, background: l.color, borderRadius: 2 }} />
          ))}
          <span>Peak</span>
          <span className="ml-auto">All times in IST · hover for details</span>
        </div>
      </div>
    </div>
  );
};

const HourlyChart = ({ hourly, currency }) => {
  if (!hourly || !hourly.length) return null;
  const max = Math.max(...hourly);
  if (max === 0) return null;
  return (
    <div className="mt-4">
      <div className="text-[10px] text-[#71717A] mb-1">Total revenue by hour-of-day (all days combined)</div>
      <div className="flex items-end gap-1 h-20 bg-[#0A0A0C] rounded p-2">
        {hourly.map((v, h) => {
          const heightPct = (v / max) * 100;
          return (
            <div key={h} className="flex-1 flex flex-col items-center justify-end gap-0.5">
              <div
                title={`${h}:00 — ${fmtMoney(v, currency)}`}
                style={{ width: "100%", height: `${heightPct}%`, background: "#F59E0B", borderRadius: "2px 2px 0 0", minHeight: v > 0 ? 2 : 0 }}
              />
              <div className="text-[7px] text-[#52525B]">{h % 3 === 0 ? h : ""}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BestSellerCard = ({ product, rank, currency }) => {
  const hasUrl = !!product.product_url;
  const Card = hasUrl ? "a" : "div";
  const linkProps = hasUrl
    ? { href: product.product_url, target: "_blank", rel: "noreferrer" }
    : {};
  return (
    <Card
      {...linkProps}
      className={`card-flat p-2.5 flex gap-2.5 transition-colors ${hasUrl ? "hover:bg-[#161618] hover:ring-1 hover:ring-[#10B981]/40 cursor-pointer" : ""}`}
      data-testid="best-seller-card"
    >
      <div className="relative shrink-0">
        {product.image ? (
          <img src={product.image} alt="" className="w-14 h-14 rounded object-cover" />
        ) : (
          <div className="w-14 h-14 rounded bg-[#1F1F22] flex items-center justify-center text-[#52525B] text-[10px]">No image</div>
        )}
        <div
          className="absolute -top-1 -left-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
          style={{ background: rank === 1 ? "#F59E0B" : rank <= 3 ? "#A855F7" : "#52525B", color: "#fff" }}
        >
          {rank}
        </div>
      </div>
      <div className="min-w-0 flex-1 flex flex-col gap-1">
        <div className="text-xs font-semibold text-[#E4E4E7] line-clamp-2 leading-tight" title={product.title}>
          {product.title}
        </div>
        {product.product_type && (
          <div className="text-[9px] text-[#71717A] truncate uppercase tracking-wider">{product.product_type}</div>
        )}
        <div className="mt-auto text-[11px] data-number font-semibold" style={{ color: "#86EFAC" }}>
          {fmtMoney(product.revenue, currency)}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#A1A1AA]">
          <span><span className="text-[#71717A]">Units</span> <span className="data-number text-[#E4E4E7]">{fmtNum(product.units)}</span></span>
          <span><span className="text-[#71717A]">Orders</span> <span className="data-number text-[#E4E4E7]">{fmtNum(product.orders)}</span></span>
        </div>
      </div>
    </Card>
  );
};

const CategoryBars = ({ categories, currency, activeCategory, onSelect, gradient }) => {
  const total = categories.reduce((s, c) => s + c.revenue, 0) || 1;
  const grad = gradient || "linear-gradient(90deg, #EC4899, #F97316)";
  const ringColor = gradient && gradient.includes("#06B6D4") ? "#06B6D4" : "#EC4899";
  return (
    <div className="space-y-1.5">
      {categories.slice(0, 12).map((c) => {
        const pct = (c.revenue / total) * 100;
        const isActive = activeCategory === c.name;
        return (
          <button
            key={c.name}
            type="button"
            onClick={() => onSelect && onSelect(c.name)}
            className={`w-full flex items-center gap-3 p-2 rounded text-left transition-colors ${
              isActive ? "bg-[#161618] ring-1" : "bg-[#0A0A0C] hover:bg-[#161618]"
            }`}
            style={isActive ? { boxShadow: `inset 0 0 0 1px ${ringColor}` } : undefined}
            data-testid="category-row"
          >
            <div className="w-32 shrink-0 text-xs font-semibold truncate">{c.name}</div>
            <div className="flex-1 h-6 bg-[#1F1F22] rounded overflow-hidden relative">
              <div
                className="h-full flex items-center px-2 text-[10px] font-semibold"
                style={{
                  width: `${Math.min(100, pct)}%`,
                  background: grad,
                  color: "#fff",
                }}
              >
                {pct >= 8 ? `${pct.toFixed(1)}%` : ""}
              </div>
              {pct < 8 && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A1A1AA]">{pct.toFixed(1)}%</span>}
            </div>
            <div className="w-24 text-right text-xs data-number" style={{ color: "#86EFAC" }}>{fmtMoney(c.revenue, currency)}</div>
            <div className="w-16 text-right text-[10px] text-[#A1A1AA]">{fmtNum(c.orders)} ord</div>
            <div className="w-12 text-right text-[10px] text-[#A1A1AA]">{c.product_count} prod</div>
          </button>
        );
      })}
    </div>
  );
};

const DailyRevenueBars = ({ daily, currency }) => {
  if (!daily || daily.length === 0) {
    return <div className="text-xs text-[#71717A]">No daily data — run sync first.</div>;
  }
  const max = Math.max(...daily.map((d) => d.revenue));
  const totalRevenue = daily.reduce((s, d) => s + d.revenue, 0);
  const totalOrders = daily.reduce((s, d) => s + d.orders, 0);
  const avgDaily = totalRevenue / daily.length;
  const bestDay = daily.slice().sort((a, b) => b.revenue - a.revenue)[0];

  // Day-of-week labels (parse YYYY-MM-DD)
  const dayLabel = (dateStr) => {
    try {
      const dt = new Date(dateStr + "T00:00:00");
      const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getDay()];
      return { day: dt.getDate(), dow };
    } catch { return { day: dateStr.slice(-2), dow: "" }; }
  };

  return (
    <div className="space-y-3">
      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="card-flat p-2">
          <div className="small-label">Total Revenue</div>
          <div className="data-number text-base mt-0.5" style={{ color: "#86EFAC" }}>{fmtMoney(totalRevenue, currency)}</div>
        </div>
        <div className="card-flat p-2">
          <div className="small-label">Total Orders</div>
          <div className="data-number text-base mt-0.5">{fmtNum(totalOrders)}</div>
        </div>
        <div className="card-flat p-2">
          <div className="small-label">Avg / Day</div>
          <div className="data-number text-base mt-0.5">{fmtMoney(avgDaily, currency)}</div>
        </div>
        <div className="card-flat p-2">
          <div className="small-label">Best Day</div>
          <div className="data-number text-base mt-0.5" style={{ color: "#FFEB00" }}>
            {dayLabel(bestDay.date).dow} {dayLabel(bestDay.date).day} · {fmtMoney(bestDay.revenue, currency)}
          </div>
        </div>
      </div>

      {/* Daily horizontal bars */}
      <div className="space-y-1">
        {daily.map((d) => {
          const pct = max > 0 ? (d.revenue / max) * 100 : 0;
          const isWeekend = ["Sat", "Sun"].includes(dayLabel(d.date).dow);
          const isBest = d.date === bestDay.date;
          return (
            <div key={d.date} className="flex items-center gap-2" data-testid="daily-bar-row">
              <div className="w-16 shrink-0 text-[10px] text-right">
                <span className={isWeekend ? "text-[#FCD34D]" : "text-[#A1A1AA]"}>{dayLabel(d.date).dow}</span>
                <span className="ml-1 text-[#E4E4E7] data-number">{dayLabel(d.date).day}</span>
              </div>
              <div className="flex-1 h-5 bg-[#0A0A0C] rounded overflow-hidden relative">
                <div
                  className="h-full flex items-center px-2 text-[10px] font-semibold"
                  style={{
                    width: `${Math.max(2, pct)}%`,
                    background: isBest
                      ? "linear-gradient(90deg, #F59E0B, #FBBF24)"
                      : isWeekend
                        ? "linear-gradient(90deg, #10B981, #34D399)"
                        : "linear-gradient(90deg, #3B82F6, #06B6D4)",
                    color: "#fff",
                    minWidth: pct >= 15 ? undefined : 0,
                  }}
                >
                  {pct >= 25 ? fmtMoney(d.revenue, currency) : ""}
                </div>
                {pct < 25 && d.revenue > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] data-number" style={{ color: "#86EFAC" }}>
                    {fmtMoney(d.revenue, currency)}
                  </span>
                )}
              </div>
              <div className="w-14 text-right text-[10px] text-[#71717A]">{fmtNum(d.orders)} ord</div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 text-[10px] text-[#71717A] pt-2 border-t border-[#1F1F22]">
        <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: "linear-gradient(90deg, #3B82F6, #06B6D4)" }} /> Weekday</span>
        <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: "linear-gradient(90deg, #10B981, #34D399)" }} /> Weekend</span>
        <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: "linear-gradient(90deg, #F59E0B, #FBBF24)" }} /> Best day</span>
      </div>
    </div>
  );
};

export default CommerceInsights;
